import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}


// Resolve a stored R2 object key to its CDN URL. Full external URLs (e.g. a
// Loom thumbnail GIF) are passed through unchanged so they can be stored in
// the same fields as uploaded files.
export const fileUrl = (key: string) => {
  // const apiUrl = import.meta.env.VITE_API_URL;
  // return `${apiUrl}/read-file?key=${key}`;
  const trimmed = (key || '').trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://cdn.greenpeel.mn/${key}`
};

// ---- Loom embeds ----
// A Loom video can be referenced several ways: a share-page URL
// (https://www.loom.com/share/{id}), a player URL (/embed/{id}), a thumbnail
// image URL (/v1/videos/{id}/thumbnail), or a whole pasted "share to website"
// HTML block that contains a link + thumbnail image. All of these resolve to
// the same playable player URL so the video actually plays inside an iframe
// (share pages and images refuse to render/play in an <iframe>).
export const loomVideoId = (value?: string | null) => {
  if (!value) return null;
  const match = value.match(
    /loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)|loom\.com\/v1\/videos\/([a-zA-Z0-9]+)\/thumbnail|loom\.com\/([a-zA-Z0-9]{20,})/i
  );
  return (match && (match[1] || match[2] || match[3])) || null;
};

// True when a stored video value must be rendered as an <iframe> embed rather
// than an HTML5 <video>: an external URL, Loom share content, or a pasted
// <iframe> snippet. Uploaded R2 object keys never match, so they keep using
// the CDN file URL with the <video> element.
export const isEmbedVideo = (value?: string | null) => {
  if (!value) return false;
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (loomVideoId(trimmed)) return true;
  return /<iframe/i.test(trimmed);
};

// If the admin pasted a full <iframe ...> snippet, extract the src URL.
// If it's already a plain URL, return it unchanged. Never render raw HTML.
export const extractEmbedSrc = (input: string) => {
  if (!input) return '';
  const trimmed = input.trim();
  if (!/src=/i.test(trimmed)) return trimmed;
  const match = trimmed.match(/src=["']([^"']+)["']/i);
  return match ? match[1].trim() : trimmed;
};

// Resolve a stored video value to a playable URL:
//   1. any Loom reference  -> https://www.loom.com/embed/{id}
//   2. plain http(s) URL   -> used as-is
//   3. pasted <iframe ...> -> its extracted src, resolved again
//   4. uploaded object key -> CDN file URL (HTML5 <video>)
export const resolveVideoUrl = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  const loomId = loomVideoId(trimmed);
  if (loomId) return `https://www.loom.com/embed/${loomId}`;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/<iframe/i.test(trimmed)) {
    const src = extractEmbedSrc(trimmed);
    if (src && src !== trimmed) return resolveVideoUrl(src);
    return undefined;
  }
  return fileUrl(trimmed);
};

// The Loom thumbnail endpoint returns an animated GIF preview of the video.
// Derive it from any Loom reference (share/embed URL, thumbnail URL, pasted
// HTML code) so it can be stored as the course thumbnail.
export const loomThumbnailUrl = (value?: string | null) => {
  const id = loomVideoId(value);
  return id ? `https://www.loom.com/v1/videos/${id}/thumbnail` : null;
};

// Rewrite Loom "share to website" blocks (a link + thumbnail image) inside
// rich-text HTML into a real Loom player so the video plays inline instead of
// just showing a clickable thumbnail. Loom's share markup looks like:
//   <a href="https://www.loom.com/share/{id}"><p>... Watch Video</p></a>
//   <a href="https://www.loom.com/share/{id}">
//     <img src="https://www.loom.com/v1/videos/{id}/thumbnail">
//   </a>
// The thumbnail anchor is replaced by the player; a plain text link (no
// thumbnail) is left untouched as a fallback way to open the video on Loom.
export const loomEmbedsToHtml = (html?: string | null) => {
  if (!html) return html || '';
  const anchorRe =
    /<a\b[^>]*href=["']([^"']*loom\.com\/share\/([a-zA-Z0-9]+))["'][^>]*>[\s\S]*?<\/a>/gi;
  return html.replace(anchorRe, (whole, _href, id: string) => {
    if (new RegExp(`loom\.com/v1/videos/${id}/thumbnail`, 'i').test(whole)) {
      return (
        `<div style="position:relative;width:100%;aspect-ratio:16/9;margin:1rem 0">` +
        `<iframe src="https://www.loom.com/embed/${id}" title="Loom video" ` +
        `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ` +
        `allowFullScreen style="position:absolute;inset:0;width:100%;height:100%;border:0">` +
        `</iframe></div>`
      );
    }
    return whole;
  });
};